import { asignacionCubre, bordesDeVigencia } from '@practiclinic/core'
import { db } from './db.js'
import { encolar } from './sync.js'

/**
 * Reprogramar una cita arrastrándola en el timeline.
 *
 * **Lo que esto NO hace: decidir si el horario nuevo está libre.** Eso lo
 * resuelve el motor en el servidor y, en última instancia, el constraint de
 * exclusión (§4.3). Acá se calcula el desplazamiento, se pinta el cambio y se
 * encola. Si el destino estaba tomado, la mutación vuelve como conflicto a la
 * bandeja y una persona decide (§5.3).
 *
 * Duplicar la validación en el cliente daría dos implementaciones que se van a
 * separar, y la del cliente siempre estaría desactualizada respecto de lo que
 * pasó en la base hace tres segundos.
 */

export interface DestinoMovimiento {
  citaId: string
  /** El recurso al que se soltó. Puede ser el mismo. */
  recursoIdDestino: string
  /** Cuántos minutos se corre respecto del horario actual. */
  deltaMin: number
}

/**
 * Aplica el movimiento en local y lo encola.
 *
 * El cambio se pinta en el acto —la recepcionista suelta la cita y la ve en su
 * lugar nuevo— aunque no haya red. Si el servidor lo rechaza, el pull siguiente
 * devuelve la cita a donde estaba y el conflicto aparece en la bandeja.
 */
export async function moverCitaLocal(destino: DestinoMovimiento): Promise<void> {
  const tramos = await db.cita_tramo.where('cita_id').equals(destino.citaId).toArray()
  if (tramos.length === 0) return

  const reservas = await db.reserva_recurso
    .where('cita_tramo_id')
    .anyOf(tramos.map((t) => t.id))
    .toArray()

  const desplazar = (iso: string) =>
    new Date(new Date(iso).getTime() + destino.deltaMin * 60_000).toISOString()

  // La asignación que se le manda al servidor: los mismos recursos, corridos en
  // el tiempo, salvo el que se cambió de columna.
  const recursosPorTramo = new Map<string, typeof reservas>()
  for (const r of reservas.filter((x) => x.activa)) {
    const lista = recursosPorTramo.get(r.cita_tramo_id) ?? []
    lista.push(r)
    recursosPorTramo.set(r.cita_tramo_id, lista)
  }

  const tipos = await db.recurso.toArray()
  const tipoDe = (recursoId: string) =>
    tipos.find((r) => r.id === recursoId)?.recurso_tipo_id ?? ''

  const recursoTipos = await db.recurso_tipo.toArray()
  const esEspacio = (recursoId: string) =>
    recursoTipos.find((t) => t.id === tipoDe(recursoId))?.es_espacio === true
  const bufferDe = (recursoId: string) =>
    recursoTipos.find((t) => t.id === tipoDe(recursoId))?.buffer_min ?? 0
  const esProfesionalPrincipal = (recursoId: string) => {
    const tipo = recursoTipos.find((t) => t.id === tipoDe(recursoId))
    return tipo?.es_persona === true && tipo.seleccionable_por_cliente
  }

  const franjas = await db.asignacion_espacio
    .filter((a) => a.estado === 'publicada' && a.deleted_at === null)
    .toArray()

  /**
   * La franja que habilita al profesional en el consultorio de destino.
   *
   * Arrastrar la asignación vieja no sirve: si la cita se movió de columna o de
   * hora, esa franja ya no contiene el tramo y el servidor la rechaza con un
   * mensaje que no explica nada. Se busca la del destino con la misma regla que
   * `asignacionQueHabilita` en el motor —la franja tiene que contener el tramo
   * **y** el buffer del consultorio—, y si no hay ninguna se manda en `null`
   * para que el rechazo diga lo que realmente pasa: ahí no hay nadie asignado.
   */
  const franjaQueHabilita = (
    personaId: string,
    espacioId: string,
    inicio: string,
    fin: string,
  ) => {
    // La franja tiene que contener el tramo **y** el buffer del consultorio.
    const tramo = {
      inicio: new Date(inicio),
      fin: new Date(new Date(fin).getTime() + bufferDe(espacioId) * 60_000),
    }
    return franjas.find(
      (a) =>
        a.persona_id === personaId &&
        a.espacio_id === espacioId &&
        asignacionCubre(
          {
            id: a.id,
            personaId: a.persona_id,
            espacioId: a.espacio_id,
            diaSemana: a.dia_semana,
            desde: a.desde,
            hasta: a.hasta,
            ...bordesDeVigencia(a.vigencia),
          },
          tramo,
        ),
    )
  }

  // El recurso que se arrastró se reemplaza por el de la columna destino; los
  // demás —el consultorio, el asistente— los vuelve a resolver el servidor si
  // hiciera falta, pero se mandan los actuales para no perder la asignación.
  const tipoDestino = tipoDe(destino.recursoIdDestino)

  const asignacion = [...tramos]
    .sort((a, b) => a.orden - b.orden)
    .map((tramo) => {
      const inicio = desplazar(tramo.inicio)
      const fin = desplazar(tramo.fin)

      const recursos = (recursosPorTramo.get(tramo.id) ?? []).map((r) => {
        const mismoTipo = tipoDe(r.recurso_id) === tipoDestino
        const recursoId = mismoTipo ? destino.recursoIdDestino : r.recurso_id
        return {
          recursoId,
          tipoId: tipoDe(recursoId),
          inicio: desplazar(r.rango.inicio),
          fin: desplazar(r.rango.fin),
        }
      })

      const espacioDestino = recursos.find((r) => esEspacio(r.recursoId))?.recursoId
      // El principal sale de los recursos ya corridos, no del tramo viejo:
      // arrastrar la cita a la columna de otra profesional también lo cambia.
      const persona = recursos.find((r) => esProfesionalPrincipal(r.recursoId))?.recursoId
      const franja =
        espacioDestino && persona
          ? franjaQueHabilita(persona, espacioDestino, inicio, fin)
          : undefined

      return {
        orden: tramo.orden,
        inicio,
        fin,
        asignacionEspacioId: franja?.id ?? null,
        profesionalPrincipalId: franja ? persona : null,
        recursos,
      }
    })

  // Optimista en Dexie: la cita se ve en su lugar nuevo al soltar el mouse.
  await db.transaction('rw', [db.cita_tramo, db.reserva_recurso], async () => {
    for (const tramo of tramos) {
      await db.cita_tramo.update(tramo.id, {
        inicio: desplazar(tramo.inicio),
        fin: desplazar(tramo.fin),
      })
    }
    for (const r of reservas) {
      const mismoTipo = tipoDe(r.recurso_id) === tipoDestino
      await db.reserva_recurso.update(r.id, {
        recurso_id: mismoTipo ? destino.recursoIdDestino : r.recurso_id,
        rango: { inicio: desplazar(r.rango.inicio), fin: desplazar(r.rango.fin) },
      })
    }
  })

  await encolar('mover_cita', { citaId: destino.citaId, asignacion })
}
